import React from 'react';
import Button from '@havenengineering/module-shared-library/dist/components/Button/Button';

import { ClientContext, FederatedLoginProvider } from '../../helpers/clientContext';
import { fetchWrapper, withIdentityApiUrl } from '../../helpers/fetch';
import styles from './FederatedLogins.module.scss';

type FederatedLoginsProps = {
  clientContext: ClientContext;
};

export const FederatedLogins = ({ clientContext }: FederatedLoginsProps) => {
  const federatedLogin = async (provider: FederatedLoginProvider) => {
    try {
      const { url } = await fetchWrapper<{ url: string }>(
        withIdentityApiUrl('/interaction/federated'),
        {
          method: 'POST',
          body: JSON.stringify({ provider }),
          credentials: 'include',
        },
      );

      return url;
    } catch (error) {
      return null;
    }
  };

  const handleClick = async (provider: FederatedLoginProvider) => {
    const url = await federatedLogin(provider);
    if (!url) {
      return;
    }
    window.location.assign(url);
  };

  return (
    <div className={styles.buttonContainer}>
      {clientContext.googleSignInEnabled && (
        <Button
          id={'google'}
          className={styles.button}
          type="button"
          onClick={() => void handleClick('google')}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-describedby="google-title google-description"
            role="img"
          >
            <title id="google-title">Google</title>
            <desc id="google-description">Sign in with google</desc>
            <path
              d="M23.7662 9.64963H22.7996V9.59983H11.9998V14.3998H18.7815C17.7921 17.1939 15.1335 19.1997 11.9998 19.1997C8.02366 19.1997 4.79992 15.9759 4.79992 11.9998C4.79992 8.02366 8.02366 4.79992 11.9998 4.79992C13.8352 4.79992 15.5049 5.4923 16.7763 6.62329L20.1705 3.22914C18.0273 1.23178 15.1605 0 11.9998 0C5.37291 0 0 5.37291 0 11.9998C0 18.6267 5.37291 23.9996 11.9998 23.9996C18.6267 23.9996 23.9996 18.6267 23.9996 11.9998C23.9996 11.1952 23.9168 10.4098 23.7662 9.64963Z"
              fill="#FFC107"
            />
            <path
              d="M1.38279 6.41449L5.32532 9.30584C6.3921 6.66468 8.97566 4.79992 11.999 4.79992C13.8344 4.79992 15.5041 5.4923 16.7755 6.62328L20.1697 3.22914C18.0265 1.23178 15.1598 0 11.999 0C7.38989 0 3.39275 2.60215 1.38279 6.41449Z"
              fill="#FF3D00"
            />
            <path
              d="M12 24.0001C15.0995 24.0001 17.9159 22.8139 20.0452 20.8849L16.3313 17.7422C15.086 18.6892 13.5644 19.2014 12 19.2001C8.87883 19.2001 6.22868 17.21 5.23029 14.4326L1.31716 17.4476C3.30313 21.3337 7.33626 24.0001 12 24.0001Z"
              fill="#4CAF50"
            />
            <path
              d="M23.7662 9.64965H22.7996V9.59985H11.9998V14.3998H18.7815C18.3082 15.7296 17.4557 16.8916 16.3293 17.7423L16.3311 17.7411L20.0451 20.8839C19.7823 21.1227 23.9996 17.9997 23.9996 11.9998C23.9996 11.1952 23.9168 10.4098 23.7662 9.64965Z"
              fill="#1976D2"
            />
          </svg>
        </Button>
      )}
      {clientContext.appleSignInEnabled && (
        <Button
          id={'apple'}
          className={styles.button}
          type="button"
          onClick={() => void handleClick('apple')}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-describedby="apple-title apple-description"
            role="img"
          >
            <title id="apple-title">Apple</title>
            <desc id="apple-description">Sign in with apple</desc>
            <path
              d="M21.2806 18.424C20.9328 19.2275 20.5211 19.9672 20.0441 20.6472C19.3939 21.5743 18.8615 22.216 18.4512 22.5724C17.8151 23.1573 17.1337 23.4568 16.4039 23.4739C15.8801 23.4739 15.2483 23.3248 14.5129 23.0224C13.7751 22.7214 13.097 22.5724 12.477 22.5724C11.8268 22.5724 11.1294 22.7214 10.3835 23.0224C9.63647 23.3248 9.03466 23.4824 8.57453 23.498C7.87475 23.5278 7.17725 23.2197 6.48102 22.5724C6.03665 22.1848 5.48084 21.5204 4.81499 20.5791C4.1006 19.5739 3.51326 18.4084 3.05313 17.0795C2.56035 15.6442 2.31332 14.2543 2.31332 12.9087C2.31332 11.3673 2.64639 10.0379 3.31351 8.92385C3.83781 8.029 4.53531 7.32312 5.40829 6.80493C6.28127 6.28674 7.22453 6.02267 8.24033 6.00578C8.79614 6.00578 9.52502 6.1777 10.4308 6.51559C11.334 6.85462 11.914 7.02655 12.1682 7.02655C12.3583 7.02655 13.0026 6.82552 14.0948 6.42473C15.1276 6.05305 15.9993 5.89916 16.7134 5.95978C18.6485 6.11595 20.1023 6.87876 21.0691 8.25303C19.3385 9.30163 18.4824 10.7703 18.4994 12.6544C18.5151 14.122 19.0474 15.3432 20.0938 16.3129C20.568 16.7629 21.0975 17.1107 21.6867 17.3578C21.5589 17.7283 21.424 18.0832 21.2806 18.424ZM16.8426 0.960131C16.8426 2.11039 16.4224 3.18439 15.5848 4.17847C14.5739 5.36023 13.3513 6.04311 12.0254 5.93536C12.0085 5.79736 11.9987 5.65213 11.9987 5.49951C11.9987 4.39526 12.4794 3.21349 13.3331 2.24724C13.7593 1.75801 14.3014 1.35122 14.9587 1.02671C15.6146 0.707053 16.235 0.530273 16.8185 0.5C16.8355 0.653772 16.8426 0.807554 16.8426 0.960116V0.960131Z"
              fill="#2D2D2D"
            />
          </svg>
        </Button>
      )}
      {clientContext.microsoftSignInEnabled && (
        <Button
          id={'microsoft'}
          className={styles.button}
          type="button"
          onClick={() => void handleClick('microsoft')}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-describedby="microsoft-title microsoft-description"
            role="img"
          >
            <title id="microsoft-title">Microsoft</title>
            <desc id="microsoft-description">Sign in with microsoft</desc>
            <path d="M0 0H11.3684V11.3684H0V0ZM11.3684" fill="#eb4d21" />
            <path d="M24 24H0V12.6316H11.3684V24ZM24" fill="#049fe8" />
            <path d="M24 0V11.3684H12.6316V0H24ZM24" fill="#7db302" />
            <path d="M24 24H12.6316V12.6316H24V24Z" fill="#f7b304" />
          </svg>
        </Button>
      )}
    </div>
  );
};
